import { useMemo, useState, useCallback } from 'react';

import Table from '~/shared/components/Table';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { generateSkeletonRows, createSkeletonRule } from '~/shared/utils/skeletonUtils';

import { useResponsiveHeaderHeight } from './hooks/useResponsiveHeaderHeight';
import { getAutoscaleRulesColumnDefs } from './autoscaleRulesColumnDefs';
import AutoscaleRuleDetailsSidePanel from './AutoscaleRuleDetailsSidePanel/AutoscaleRuleDetailsSidePanel';
import { isToggleColumnClickedFlag, setToggleColumnClicked } from './ToggleRuleRenderer';

import type { AutoscaleRule } from './hooks/useAutoscaleRulesQuery';

export const DATA_LIMIT = 50;

type AutoscaleRuleWithSkeleton = AutoscaleRule & { _isSkeleton?: boolean };

interface AutoscaleRulesTableProps {
  loading?: boolean;
  rules?: AutoscaleRule[];
  onEditRule?: (ruleId: string, rawRule: Record<string, unknown>) => void;
  isOrganizationAccount?: boolean;
}

function AutoscaleRulesTable({
  loading = false,
  rules = [],
  onEditRule,
  isOrganizationAccount = false,
}: AutoscaleRulesTableProps) {
  const { user, activeTenantName } = useAuthContext();
  const isUserReadOnly = !!user?.roles?.find((r: { key: string }) => r.key === 'ReadOnly');
  const isDisabledForUser = isUserReadOnly || isOrganizationAccount;
  const { headerHeight, windowWidth } = useResponsiveHeaderHeight();
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Derive selectedRule from rules array based on selectedRuleId
  // This ensures we always have the latest data without triggering re-renders
  const selectedRule = useMemo(() => {
    if (!selectedRuleId || rules.length === 0) {
      return null;
    }
    return rules.find((r) => r.ruleId === selectedRuleId) || null;
  }, [selectedRuleId, rules]);

  const rowData = useMemo(() => {
    if (loading && rules.length === 0) {
      return generateSkeletonRows(10, createSkeletonRule()) as AutoscaleRuleWithSkeleton[];
    }
    return rules;
  }, [rules, loading]);

  const columnDefs = useMemo(
    () => getAutoscaleRulesColumnDefs(activeTenantName, isDisabledForUser, windowWidth),
    [activeTenantName, isDisabledForUser, windowWidth]
  );

  const handleRowClick = (rule: AutoscaleRule) => {
    // Prevent clicks on skeleton rows
    if ((rule as any)._isSkeleton) {
      return;
    }

    const toggleClicked = isToggleColumnClickedFlag();
    setToggleColumnClicked(false);

    if (toggleClicked) {
      return;
    }

    if (selectedRuleId === rule.ruleId && isSidePanelOpen) {
      return;
    }

    setSelectedRuleId(rule.ruleId);
    if (!isSidePanelOpen) {
      setIsSidePanelOpen(true);
    }
  };

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
    setTimeout(() => setSelectedRuleId(null), 300);
  };

  const getRowClass = useCallback(
    (rule: AutoscaleRule) => {
      if ((rule as any)._isSkeleton) {
        return 'skeleton-row pointer-events-none';
      }

      const isSelected = selectedRuleId === rule.ruleId && isSidePanelOpen;
      return isSelected ? 'active-rule-row group' : 'group';
    },
    [selectedRuleId, isSidePanelOpen]
  );

  return (
    <>
      <div className="flex flex-col min-h-[348px] h-full gap-2.5 2xl:gap-3.5 text-sm">
        <Table
          rowData={rowData}
          columnDefs={columnDefs}
          headerHeight={headerHeight.height}
          loading={false}
          disableClientSort
          suppressNoRowsWhileLoading
          itemsName="rules"
          onRowClick={handleRowClick}
          rowHoverClass="custom-row-hover-bg"
          getRowClass={getRowClass}
          getRowId={(params) => params.data.ruleId}
          pagination={{
            perPage: DATA_LIMIT,
            onChange: () => {},
            totalPages: Math.ceil((rowData?.length || 0) / DATA_LIMIT),
            dataCount: loading ? 0 : rowData?.length || 0,
            itemsName: 'rules',
            currentPage: 0,
          }}
        />
      </div>

      <AutoscaleRuleDetailsSidePanel
        rule={selectedRule}
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
        onEdit={onEditRule}
      />
    </>
  );
}

export default AutoscaleRulesTable;
